import React, { useState } from 'react';
import { generatePath } from 'react-router';
import { Button, Tooltip } from 'src/components-dummy';
import { IPlaybookBankAnnotated } from 'src/services';
import {
  BadgesRow,
  CardContainer,
  CardDescription,
  CardError,
  CardFooterRow,
  CardHeaderRow,
  CardName,
  DataFieldsLink,
  MetricBadge,
  RiskBadge,
} from './PlaybookCard.styles';

export interface PlaybookBankServiceLike {
  createDraftExperiment(args: {
    shopId: number;
    playbookId: string;
  }): Promise<{ experimentId: string }>;
}

// The Variants step (step 3): a playbook's variant patch is already applied by the time
// createDraftExperiment resolves, so the merchandiser should land where they can see/tweak
// it rather than re-walking Goal/What-to-test.
const VARIANTS_STEP = 3;

export interface PlaybookCardProps {
  playbook: IPlaybookBankAnnotated;
  shopId: number;
  experimentWizardRoute?: string;
  dataFieldsRoute?: string;
  onNavigate: (path: string) => void;
  service: PlaybookBankServiceLike;
}

const METRIC_LABELS: Record<string, string> = {
  user_conversion: 'CVR',
  average_order_value: 'AOV',
  average_revenue_per_user: 'ARPU',
};

export const PlaybookCard = ({
  playbook,
  shopId,
  experimentWizardRoute,
  dataFieldsRoute,
  onNavigate,
  service,
}: PlaybookCardProps): JSX.Element => {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { satisfied } = playbook;
  const metricLabel = METRIC_LABELS[playbook.metric] ?? playbook.metric;

  const handleStart = async () => {
    if (!satisfied || starting) {
      return;
    }

    setStarting(true);
    setError(null);

    try {
      const { experimentId } = await service.createDraftExperiment({
        shopId,
        playbookId: playbook.id,
      });

      if (experimentWizardRoute) {
        onNavigate(
          generatePath(experimentWizardRoute, { experimentId, shopId, step: VARIANTS_STEP })
        );
      }
      setStarting(false);
    } catch {
      setError('Could not start this playbook. Please try again.');
      setStarting(false);
    }
  };

  const dataFieldsPath = dataFieldsRoute ? generatePath(dataFieldsRoute, { shopId }) : undefined;

  const card = (
    <CardContainer disabled={!satisfied} data-testid='playbook-card'>
      <CardHeaderRow>
        <CardName>{playbook.name}</CardName>
      </CardHeaderRow>

      <CardDescription>{playbook.description}</CardDescription>

      <BadgesRow>
        <RiskBadge risk={playbook.risk}>{playbook.risk} risk</RiskBadge>
        <MetricBadge>{metricLabel}</MetricBadge>
      </BadgesRow>

      {error && <CardError role='alert'>{error}</CardError>}

      <CardFooterRow>
        <Button
          variant='primary'
          size='small'
          disabled={!satisfied || starting}
          loading={starting}
          onClick={handleStart}
        >
          Start
        </Button>

        {!satisfied && dataFieldsPath && (
          <DataFieldsLink
            href={dataFieldsPath}
            onClick={e => {
              e.preventDefault();
              onNavigate(dataFieldsPath);
            }}
          >
            Add missing data fields
          </DataFieldsLink>
        )}
      </CardFooterRow>
    </CardContainer>
  );

  if (satisfied) {
    return card;
  }

  return (
    <Tooltip
      value='This playbook needs data fields the shop doesn’t have yet. Add them to enable it.'
      disabled={satisfied}
    >
      {card}
    </Tooltip>
  );
};
